import { cloneDeep, isEqual } from 'lodash-es'
import { computed, reactive, ref } from 'vue'
import type { z } from 'zod'

/**
 * Form state for PrimeVue forms, validated with a zod schema.
 *
 * Fields bind with `v-model="form.values.x"` and show `form.errorFor('x')`.
 * Errors appear once a field has been touched (blurred) or after a submit
 * attempt, so an empty form doesn't open covered in red. Cross-field rules
 * (a refine on the whole object) work because every check parses the whole
 * form and keeps the messages for the fields asked about.
 *
 * Auto-saving forms use `isDirty` and `markSaved()`: the baseline is what was
 * last saved, so a store update that echoes the save doesn't count as an edit.
 * `reset(next)` replaces both values and baseline, e.g. when the record
 * behind the form changes.
 */
export function useZodForm<Schema extends z.ZodType<object, object>>(
  schema: Schema,
  initialValues: z.input<Schema>,
) {
  type Values = z.input<Schema>

  const values = reactive(cloneDeep(initialValues)) as Values
  const baseline = ref(cloneDeep(initialValues)) as { value: Values }
  const errors = reactive<Record<string, string>>({})
  const touched = reactive<Record<string, boolean>>({})
  const submitted = ref(false)

  const pathOf = (path: PropertyKey[]) => path.map(String).join('.')

  // Run the whole schema; return every message by dotted field path
  function collectErrors(): Record<string, string> {
    const result = schema.safeParse(values)
    if (result.success) return {}
    const found: Record<string, string> = {}
    for (const issue of result.error.issues) {
      const key = pathOf(issue.path)
      if (!(key in found)) found[key] = issue.message
    }
    return found
  }

  function setErrors(found: Record<string, string>, onlyFields?: string[]) {
    const inScope = (key: string) => !onlyFields || onlyFields.some(f => key === f || key.startsWith(`${f}.`))
    for (const key of Object.keys(errors)) {
      if (inScope(key)) delete errors[key]
    }
    for (const [key, message] of Object.entries(found)) {
      if (inScope(key)) errors[key] = message
    }
  }

  /** Check every field and show all messages. Returns the parsed data when valid. */
  function validate(): { valid: true, data: z.output<Schema> } | { valid: false, data?: undefined } {
    submitted.value = true
    const result = schema.safeParse(values)
    if (result.success) {
      setErrors({})
      return { valid: true, data: result.data }
    }
    setErrors(collectErrors())
    return { valid: false }
  }

  /** Check the given fields only (others keep whatever they showed). */
  function validateFields(...fields: string[]): boolean {
    const found = collectErrors()
    setErrors(found, fields)
    return !Object.keys(found).some(key => fields.some(f => key === f || key.startsWith(`${f}.`)))
  }

  /** Mark a field as visited (on blur) and check it. */
  function touch(field: string): boolean {
    touched[field] = true
    return validateFields(field)
  }

  // A field counts as touched when it or a parent was (touching "address"
  // reveals "address.city")
  const isTouched = (field: string) => {
    const parts = field.split('.')
    return parts.some((_, i) => touched[parts.slice(0, i + 1).join('.')])
  }

  /** The message to show for a field, once it has been touched or submitted. */
  function errorFor(field: string): string | undefined {
    if (!isTouched(field) && !submitted.value) return undefined
    return errors[field]
  }

  const isDirty = computed(() => !isEqual(values, baseline.value))

  /** Values that differ from the baseline (top-level keys). */
  function changedValues(): Partial<Values> {
    const out: Record<string, unknown> = {}
    const current = values as Record<string, unknown>
    const base = baseline.value as Record<string, unknown>
    for (const key of Object.keys(current)) {
      if (!isEqual(current[key], base[key])) out[key] = current[key]
    }
    return out as Partial<Values>
  }

  /** The current values are what's saved now. */
  function markSaved() {
    baseline.value = cloneDeep(values) as Values
  }

  /** Start over from new values (or the original ones). */
  function reset(next?: Values) {
    const source = cloneDeep(next ?? initialValues) as Record<string, unknown>
    const current = values as Record<string, unknown>
    for (const key of Object.keys(current)) {
      if (!(key in source)) delete current[key]
    }
    Object.assign(current, source)
    baseline.value = cloneDeep(source) as Values
    for (const key of Object.keys(errors)) delete errors[key]
    for (const key of Object.keys(touched)) delete touched[key]
    submitted.value = false
  }

  return {
    values,
    errors,
    touched,
    submitted,
    isDirty,
    validate,
    validateFields,
    touch,
    errorFor,
    changedValues,
    markSaved,
    reset,
  }
}

export type ZodForm<Schema extends z.ZodType<object, object>> = ReturnType<typeof useZodForm<Schema>>
